import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Ng2StateDeclaration, UIRouterModule } from '@uirouter/angular';
import { Transition } from '@uirouter/core';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { Permissions } from 'account-hybrid/core/authentication';
import { SpaceType } from 'account-hybrid/common/components/spaces/models/space-type';
import { AccountSharedModule, PipesModule } from 'apps/account/src/account-shared';
import { AccountCommonModule } from '../../common/common.module';
import { GuestCardComponent } from './components/guest-card/guest-card.component';
import { RatingsMeterComponent } from './components/ratings-meter/ratings-meter.component';
// The guest-detail modal (DetailsComponent + body + GuestPhoto + activityType/
// dateFormat pipes) lives in the shared PeopleDetailsModule — also imported by
// Advocacy + Stories so all three open the SAME native modal (M5b cleanup).
import { PeopleDetailsModule } from './details/people-details.module';
import { DetailsComponent } from './details/details.component'; // class ref for the forChild `component:` states (declared in PeopleDetailsModule)
import { DirectoryComponent } from './directory/directory.component';
import { FeedbackComponent } from './feedback/feedback.component';
import { PeopleComponent } from './people.component';
import { QuotPipe } from './pipes/quot.pipe';
import { GuestsApiService } from './services/guests-api.service';

/**
 * Native-Angular People feature module (AngularJS -> Angular rewrite).
 * Plan: .github/upgrades/account-people-rewrite-plan.md
 *
 * Milestone 1 (done): GuestCard + pipes (additive).
 * Milestone 2 (done): `people.directory` (/library) rendered by native Angular
 *   DirectoryComponent, gate-green page-load.
 * Milestone 3 (here) — SHELL-FIRST PIVOT: `people` is now a top-level native
 *   Angular state (matching the Stories pattern) so the Angular sub-tree can
 *   host its AngularJS leaf states. Leaf-first failed because the hybrid won't
 *   render an AngularJS child into an Angular <ui-view> that is itself nested
 *   inside an AngularJS ui-view. All `people.*` registrations now live HERE
 *   (Angular forChild) — AngularJS leaves are referenced by string component
 *   name ('ftPeopleFeedback', 'ftPeopleDetails'), same trick stories.details
 *   uses. Removed from account-v2 state.config.ts.
 *
 * Future milestones (M4 Feedback, M5 Details) port the leaves; M6 cleans up.
 */
@NgModule({
  declarations: [
    QuotPipe,
    GuestCardComponent,
    RatingsMeterComponent,
    DirectoryComponent,
    FeedbackComponent,
    PeopleComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    PipesModule,
    AccountSharedModule,
    AccountCommonModule,
    PeopleDetailsModule,
    UIRouterModule.forChild({
      states: [
        {
          name: 'people',
          url: '/{type:\\w}/{slug}/people',
          params: {
            type: { dynamic: true },
            slug: { dynamic: true },
          },
          component: PeopleComponent,
          data: {
            requiresAuth: true,
            // v1's state data listed Company too, but that entry was latent: the module-load
            // gate (account-v2 features/index.ts) never loaded People for ANY company space
            // (commented out of group.features forCompanySpace, absent from coop.features),
            // so /c/{slug}/people was unreachable on master. This eagerly-registered port
            // relies on the space-type hook instead — Company must not be allowed here.
            requiredSpaceType: [SpaceType.Property, SpaceType.Mixed],
            requiredPermissions: Permissions.PeopleRead,
            pageClass: 'ft-advocacy-body',
          },
        },
        { name: 'people.directory', url: '/library', component: DirectoryComponent },
        // All people.* states are native Angular now (M3/M4/M5b). The detail
        // modal uses the shared native DetailsComponent (PeopleDetailsModule).
        {
          name: 'people.directory.details',
          url: '/library/{advocateUUID:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}}?curate',
          component: DetailsComponent,
          // WS5 #10 Stage B B5c: ng1 string-token resolves → native Ng2 form
          // (Transition + ParseService class token).
          resolve: [
            { token: 'advocateUuid', deps: [Transition], resolveFn: (trans: Transition) => trans.params().advocateUUID },
            { token: 'curate', deps: [Transition, ParseService], resolveFn: (trans: Transition, parse: ParseService) => parse.bool(trans.params().curate) },
          ],
        },
        // people.feedback — M4 native Angular FeedbackComponent (was the AngularJS
        // `ftPeopleFeedback` component referenced by string).
        {
          name: 'people.feedback',
          url: '/feedback',
          component: FeedbackComponent,
        },
        {
          name: 'people.feedback.details',
          url: '/feedback/{advocateUUID:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}}?curate',
          component: DetailsComponent,
          // WS5 #10 Stage B B5c: ng1 string-token resolves → native Ng2 form
          // (Transition + ParseService class token).
          resolve: [
            { token: 'advocateUuid', deps: [Transition], resolveFn: (trans: Transition) => trans.params().advocateUUID },
            { token: 'curate', deps: [Transition, ParseService], resolveFn: (trans: Transition, parse: ParseService) => parse.bool(trans.params().curate) },
          ],
        },
      ] as Ng2StateDeclaration[],
    }),
  ],
  providers: [GuestsApiService],
  // Re-export PeopleDetailsModule so DetailsComponent / body / GuestPhoto stay
  // available to any consumer of PeopleModule (GuestCard is people-local).
  exports: [GuestCardComponent, PeopleDetailsModule],
})
export class PeopleModule {}
